<template>
  <!-- ===== ADD / EDIT MODE ===== -->
  <template v-if="mode !== 'detail'">
  
    <!-- Back link -->
    <div class="mb-3">
      <a class="d-flex align-items-center gap-2 text-primary fw-semibold" style="cursor:pointer;width:fit-content"
        @click="goBack">
        <i class="bi bi-arrow-left"></i> Back
      </a>
    </div>
  
    <div class="card-modern">
  
      <!-- Header -->
      <div class="mb-4">
        <h2 class="fw-bold mb-1" style="font-size:1.6rem;color:#0f172a">{{ pageTitle }}</h2>
        <p class="card-desc-modern mb-0">
          {{
          mode === 'add'
          ? 'Tambah dokumen penawaran harga biaya angkut.'
          : 'Edit dokumen penawaran harga biaya angkut.'
          }}
        </p>
      </div>
  
      <!-- Fields -->
      <div class="row g-3 mb-3">
        <div class="col-md-6">
          <label class="form-label">Nama Perusahaan <span class="text-danger">*</span></label>
          <Multiselect v-model="form.mst_corporate_id" :options="corporates" valueProp="mst_corporate_id"
            label="mst_corporate_name" trackBy="mst_corporate_name" placeholder="-- Pilih Customer --" :searchable="true"
            :canClear="true" :disabled="mode === 'edit'" class="multiselect" />
        </div>
        <div class="col-md-6">
          <label class="form-label">Contract Type <span class="text-danger">*</span></label>
          <Multiselect v-model="form.contract_type" :options="contractTypes" valueProp="value" label="description"
            trackBy="value" placeholder="-- Pilih Contract --" :searchable="true" :canClear="true"
            :disabled="mode === 'edit'" class="multiselect" />
        </div>
      </div>
  
      <!-- Fixed-Term date range -->
      <div v-if="isFixedTerm" class="row g-3 mb-3">
        <div class="col-md-6">
          <label class="form-label">Start Date <span class="text-danger">*</span></label>
          <input type="date" class="form-control" v-model="form.start_date">
        </div>
        <div class="col-md-6">
          <label class="form-label">End Date <span class="text-danger">*</span></label>
          <input type="date" class="form-control" v-model="form.end_date" :min="form.start_date || ''">
        </div>
      </div>
  
      <!-- Existing files (edit mode) -->
      <div v-if="mode === 'edit' && form.files.length" class="mb-4">
        <label class="form-label mb-2">Current Files</label>
        <div class="row g-2">
          <div v-for="f in form.files" :key="f.id" class="col-md-12">
            <div class="d-flex align-items-center justify-content-between py-2 px-3 border rounded-3 h-100">
              <div class="d-flex align-items-center gap-2 text-truncate" style="min-width:0"><i
                  class="bi fs-4 flex-shrink-0" :class="fileIconClass(f.file_key)"></i>
                <span class="text-truncate" style="font-size:0.875rem" :title="getFileName(f.file_key)">{{
                  getFileName(f.file_key) }}</span>
              </div>
              <div class="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                <span class="badge-modern" :class="f.status == 1 ? 'badge-success' : 'badge-danger'">
                  {{ f.status == 1 ? 'Active' : 'Inactive' }}
                </span>
                <button class="btn btn-outline-primary btn-sm" title="Preview" @click="downloadFile(f.file_key)">
                  <i class="bi bi-eye"></i>
                </button>
                <button class="btn btn-sm" :class="f.status == 1 ? 'btn-outline-secondary' : 'btn-outline-success'"
                  :title="f.status == 1 ? 'Nonaktifkan' : 'Aktifkan'" @click="toggleFileStatus(f)">
                  <i class="bi" :class="f.status == 1 ? 'bi-toggle-on' : 'bi-toggle-off'"></i>
                </button>
                <button class="btn btn-outline-danger btn-sm" title="Remove File" @click="removeExistingFile(f)">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <hr>
  
      <!-- Upload section -->
      <div class="mb-3">
        <label class="form-label">Tambah File <span class="text-danger">*</span></label>
  
        <!-- Drop zone -->
        <div class="upload-box mb-3" :class="{ dragging: isDragging }" @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop" @click="$refs.fileInput.click()">
          <input ref="fileInput" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            @change="handleFileUpload">
          <i class="bi bi-cloud-upload upload-icon"></i>
          <p class="upload-title mb-1">
            Drag &amp; drop file here, or
            <span class="text-primary" style="cursor:pointer">click to browse</span>
          </p>
          <p class="upload-desc mb-0">
            PDF / JPG / JPEG / PNG (max 10MB), bisa pilih lebih dari 1 file
          </p>
        </div>
      </div>
      <!-- Selected files preview -->
      <div v-if="form.file.length" class="mb-4">
        <div v-for="(f, index) in form.file" :key="index"
          class="d-flex align-items-center justify-content-between px-3 py-2 border rounded-3 bg-light mb-2"
          style="max-width: 400px;">
          <div class="d-flex align-items-center gap-2">
            <i class="bi fs-4" :class="fileIconClass(f.name)"></i>
            <div>
              <div class="fw-semibold" style="font-size:0.875rem">{{ f.name }}</div>
              <small class="text-muted">{{ formatFileSize(f.size) }}</small>
            </div>
          </div>
          <button class="btn btn-outline-danger btn-sm" @click="removeNewFile(index)">
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>
  
      <!-- PKS -->
      <div class="form-check mb-3">
        <input class="form-check-input" type="checkbox" id="hasPks" :checked="form.has_pks == 1"
          @change="form.has_pks = $event.target.checked ? 1 : 0">
        <label class="form-check-label fw-semibold" for="hasPks">Memiliki Dokumen Perjanjian Kerja Sama?</label>
      </div>
  
      <div v-if="form.has_pks == 1" class="alert alert-warning py-2 mb-3">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        Pastikan dokumen perjanjian kerja sama telah tersedia.
      </div>
  
      <div v-else class="mb-3">
        <label class="form-label">Alasan Tidak Ada Perjanjian Kerja Sama <span class="text-danger">*</span></label>
        <textarea class="form-control" rows="2" v-model="form.pks_reason"></textarea>
      </div>
  
      <hr>
      <div class="card shadow-sm border-0 mt-3 mb-3">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h3 class="section-title">
              Dokumen Legalitas
              <span v-if="checklist.company_type" class="badge bg-light text-secondary ms-1">
                {{ checklist.company_type }}
              </span>
            </h3>
            <!-- <span v-if="checklist.items.length"
                            :class="['badge', checklistLocalStatus.is_complete ? 'bg-success' : 'bg-warning text-dark']">
                            <i
                              :class="['bi', checklistLocalStatus.is_complete ? 'bi-check-circle' : 'bi-exclamation-triangle', 'me-1']"></i>
                            {{ checklistLocalStatus.is_complete ? 'Lengkap' : 'Belum Lengkap' }}
                          </span> -->
          </div>
  
          <div v-if="!checklist.items.length" class="text-muted text-center py-3">
            List dokumen legalitas akan muncul setelah perusahaan dipilih.
          </div>
  
          <div v-for="item in checklist.items" :key="item.key" class="mb-3">
            <label class="form-label fw-semibold d-flex align-items-center gap-2 mb-1">
              {{ item.label }}
              <span v-if="!item.required" class="text-muted fw-normal">(opsional)</span>
              <span v-if="item.required && !checklistPendingFiles[item.key] && !item.file_path" class="badge bg-danger">
                Belum Ada
              </span>
            </label>
  
            <!-- Pending (belum upload) -->
            <div v-if="checklistPendingFiles[item.key]"
              class="d-flex align-items-center justify-content-between px-3 py-2 border rounded-3 bg-light mb-2"
              style="max-width:400px">
              <div class="d-flex align-items-center gap-2 text-truncate">
                <i class="bi fs-4" :class="fileIconClass(checklistPendingFiles[item.key].name)"></i>
                <div class="text-truncate">
                  <div class="fw-medium text-truncate">{{ checklistPendingFiles[item.key].name }}</div>
                  <small class="text-muted">{{ formatFileSize(checklistPendingFiles[item.key].size) }}</small>
                </div>
              </div>
              <button type="button" class="btn btn-sm btn-outline-danger" title="Batalkan"
                @click="cancelPendingChecklistFile(item.key)">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
  
            <!-- Sudah di server -->
            <div v-else-if="item.file_path"
              class="d-flex align-items-center justify-content-between px-3 py-2 border rounded-3 mb-2"
              style="max-width:400px">
              <div class="d-flex align-items-center gap-2 text-truncate">
                <i class="bi fs-4" :class="fileIconClass(item.file_path)"></i>
                <span class="text-truncate">{{ getFileName(item.file_path) }}</span>
              </div>
              <div class="d-flex gap-1">
                <button type="button" class="btn btn-sm btn-outline-primary" title="Lihat"
                  @click="downloadFile(item.file_path)">
                  <i class="bi bi-eye"></i>
                </button>
                <button type="button" class="btn btn-sm btn-outline-danger" title="Hapus"
                  @click="removeChecklistFile(item.key)">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
  
            <!-- Drop zone -->
            <div v-else class="upload-box upload-box-sm mb-2" :class="{
                                                    dragging: checklistDragging === item.key,
                                                    'opacity-50': mode === 'add' && !form.mst_corporate_id,
                                                  }" @dragover.prevent="checklistDragging = item.key"
              @dragleave.prevent="checklistDragging = null" @drop.prevent="handleChecklistDrop(item.key, $event)"
              @click="triggerChecklistInput(item.key)">
              <input :ref="'checklistInput_' + item.key" type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" class="d-none"
                @change="handleChecklistUpload(item.key, $event)">
              <i class="bi bi-cloud-upload upload-icon upload-icon-sm"></i>
              <span class="upload-text">Drag &amp; drop file di sini, atau klik untuk upload PDF/JPG/JPEG/PNG (maks
                10MB)</span>
            </div>
          </div>
  
          <hr v-if="checklist.items.length" class="my-4">
          <div v-if="checklist.items.length" class="legal-note mt-3">
            <div class="d-flex align-items-center mb-2">
              <i class="bi bi-info-circle-fill text-primary me-2"></i>
              <strong>Catatan</strong>
            </div>
            <ul class="mb-0 ps-3">
              <li>NPWP perorangan disesuaikan dengan jenis jasa dan ketentuan perpajakan yang berlaku.</li>
              <li>
                Apabila pemasok berbentuk PT/CV menggunakan rekening pribadi,
                wajib melampirkan Surat Pernyataan Penggunaan Rekening Pribadi beserta bukti kepemilikan rekening.
              </li>
            </ul>
          </div>
        </div>
      </div>
  
      <!-- Footer actions -->
      <div class="d-flex justify-content-end gap-2">
        <button class="btn btn-secondary" @click="goBack">Cancel</button>
        <button class="button-modern d-flex align-items-center gap-2" :disabled="isLoading" @click="saveData">
          <span v-if="isLoading" class="spinner-border spinner-border-sm"></span>
          <i v-else class="bi bi-floppy"></i>
          {{ submitLabel }}
        </button>
      </div>
  
    </div>
  </template>
  
  <!-- ===== DETAIL MODE ===== -->
  <template v-else>
  
    <!-- Breadcrumb -->
    <div class="d-flex align-items-center gap-2 mb-3" style="font-size:0.875rem">
      <a class="text-primary fw-semibold" style="cursor:pointer"
        @click="$router.push('/transport/transport-offers')">Penawaran
        Harga Biaya Angkut</a>
      <i class="bi bi-chevron-right text-muted" style="font-size:12px"></i>
      <span class="text-muted">Details</span>
    </div>
  
    <div class="card-modern">
  
      <!-- Header -->
      <div class="d-flex align-items-start justify-content-between mb-4">
        <div>
          <h2 class="fw-bold mb-1" style="font-size:1.6rem;color:#0f172a">Detail Dokumen</h2>
          <p class="card-desc-modern mb-0">Detail informasi dokumen penawaran harga biaya angkut.</p>
        </div>
        <button class="btn btn-secondary d-flex align-items-center gap-2" @click="goBack">
          Back
        </button>
      </div>
  
      <!-- Info strip: Customer & Contract Type -->
      <div class="row g-0 border rounded-3 overflow-hidden mb-4">
        <div class="col-md-4 d-flex align-items-center gap-3 p-4">
          <div class="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style="width:48px;height:48px;background:var(--primary-color);color:#fff;font-size:1.3rem">
            <i class="bi bi-people-fill"></i>
          </div>
          <div>
            <div class="text-muted"
              style="font-size:0.75rem;font-weight:600;text-transform:uppercase;letter-spacing:0.05em">Customer</div>
            <div class="fw-bold" style="font-size:1rem;color:#0f172a">{{ detail.mst_corporate_name || '-' }}</div>
          </div>
        </div>
        <div class="col-md-4 d-flex align-items-center gap-3 p-4 border-start">
          <div class="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style="width:48px;height:48px;background:var(--primary-color);color:#fff;font-size:1.3rem">
            <i class="bi bi-file-text-fill"></i>
          </div>
          <div>
            <div class="text-muted"
              style="font-size:0.75rem;font-weight:600;text-transform:uppercase;letter-spacing:0.05em">Contract Type
            </div>
            <div class="fw-bold" style="font-size:1rem;color:#0f172a">{{ detail.contract_type_name || '-' }}</div>
            <div v-if="detail.start_date || detail.end_date" class="text-muted" style="font-size:0.8rem">
              {{ formatDate(detail.start_date) }} s/d {{ formatDate(detail.end_date) }}
            </div>
          </div>
        </div>
        <div class="col-md-4 d-flex align-items-center gap-3 p-4 border-start">
          <div class="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            :style="detail.has_pks == 1 ? 'width:48px;height:48px;background:#16a34a;color:#fff;font-size:1.3rem' : 'width:48px;height:48px;background:#94a3b8;color:#fff;font-size:1.3rem'">
            <i :class="detail.has_pks == 1 ? 'bi bi-file-earmark-check-fill' : 'bi bi-file-earmark-x-fill'"></i>
          </div>
          <div>
            <div class="text-muted"
              style="font-size:0.75rem;font-weight:600;text-transform:uppercase;letter-spacing:0.05em">Perjanjian Kerja
              Sama</div>
            <span v-if="detail.has_pks == 1" class="badge-modern badge-success">Ada Perjanjian Kerja Sama</span>
            <template v-else>
              <span class="badge-modern badge-danger">Tidak Ada Perjanjian Kerja Sama</span>
              <div v-if="detail.pks_reason" class="text-muted mt-1" style="font-size:0.8rem;max-width:220px">
                <i class="bi bi-quote me-1"></i>{{ detail.pks_reason }}
              </div>
            </template>
          </div>
        </div>
      </div>
  
      <!-- Documents section -->
      <div class="d-flex align-items-start justify-content-between mb-3">
        <div>
          <h4 class="card-title-modern mb-1">Documents ({{ (detail.files || []).length }})</h4>
          <p class="card-desc-modern mb-0" style="font-size:0.85rem">List dokumen penawaran harga biaya angkut.</p>
        </div>
      </div>
  
      <template v-if="detail.files && detail.files.length">
        <div v-for="f in detail.files" :key="f.id"
          class="d-flex align-items-center justify-content-between py-2 px-3 mb-2 border rounded-3">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-file-earmark-pdf-fill text-danger fs-2"></i>
            <span style="font-size:1rem">{{ getFileName(f.file_key) }}</span>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="badge-modern" :class="f.status == 1 ? 'badge-success' : 'badge-danger'">
              {{ f.status == 1 ? 'Active' : 'Inactive' }}
            </span>
            <button class="btn btn-outline-primary btn-sm" @click="downloadFile(f.file_key)">
              <i class="bi bi-eye"></i>
            </button>
            <button class="btn btn-outline-secondary btn-sm" @click="downloadFile(f.file_key)">
              <i class="bi bi-download"></i>
            </button>
            <!-- <button class="btn btn-outline-secondary btn-sm" @click="toggleFileStatus(f, true)">
                                        <i class="bi" :class="f.status == 1 ? 'bi-toggle-on' : 'bi-toggle-off'"></i>
                                      </button> -->
          </div>
        </div>
      </template>
  
      <div v-else class="alert alert-light border text-center py-5">
        <i class="bi bi-folder2-open d-block mb-2" style="font-size:2rem;color:#94a3b8"></i>
        Belum ada dokumen.
      </div>
      <!-- ───────── Checklist Dokumen Legal (read-only, per Corporate) ───────── -->
      <div class="card shadow-sm border-0 mb-3">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="section-title mb-0">
              <i class="bi bi-file-earmark-pdf me-2"></i>
              Dokumen Legalitas
              <span class="badge bg-light text-secondary ms-1">{{ checklist.company_type }}</span>
            </h6>
            <span :class="['badge', checklist.is_complete ? 'bg-success' : 'bg-warning text-dark']">
              <i :class="['bi', checklist.is_complete ? 'bi-check-circle' : 'bi-exclamation-triangle', 'me-1']"></i>
              {{ checklist.is_complete ? 'Lengkap' : 'Belum Lengkap' }}
            </span>
          </div>
  
          <div v-if="!checklist.items.length" class="text-muted text-center py-3" style="font-size:0.875rem">
            Belum ada checklist dokumen legal untuk customer ini.
          </div>
  
          <table v-else class="table table-bordered align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th style="width:40px">No</th>
                <th>Dokumen</th>
                <th style="width:160px">Status</th>
                <th style="width:120px">Aksi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in checklist.items" :key="item.key">
                <td>{{ idx + 1 }}</td>
                <td>
                  {{ item.label }}
                  <span v-if="!item.required" class="text-muted">(opsional)</span>
                </td>
                <td>
                  <span v-if="item.file_path" class="badge bg-success">Sudah Upload</span>
                  <span v-else-if="item.required" class="badge bg-danger">Belum Ada</span>
                  <span v-else class="badge bg-secondary">-</span>
                </td>
                <td>
                  <button v-if="item.file_path" class="btn btn-sm btn-outline-primary" title="Lihat File"
                    @click="downloadFile(item.file_path)">
                    <i class="bi bi-eye"></i> Lihat
                  </button>
                  <span v-else class="text-muted">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import TransportOffersFormJs from "./TransportOffersForm";
export default TransportOffersFormJs;
</script>